import { Action, DEFAULT_LIMIT, DEFAULT_PAGE } from "@pilatestime/shared-types";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { Plus, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { PaginationMeta } from "@/components/data-table";
import {
	ListPageCard,
	ListPageCardContent,
	ListPageCardHeader,
} from "@/components/ListPageCard";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import { useCan } from "@/hooks/useAccessControl";
import { useApiParameters } from "@/hooks/useApiParameters";
import { CustomerList } from "./-components/CustomerList";

const searchSchema = z.object({
	page: z.number().catch(DEFAULT_PAGE),
	limit: z.number().catch(DEFAULT_LIMIT),
	search: z.string().catch(""),
});

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/customers/")({
	validateSearch: searchSchema,
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const orgId = params.orgId;

		return { userId, token, orgId };
	},
	component: CustomersPage,
});

function CustomersPage() {
	const { t } = useTranslation();
	const { orgId } = useParams({
		from: "/_auth/app/_app/org/$orgId/customers/",
	});
	const search = Route.useSearch();
	const { userId, token } = Route.useLoaderData();

	// Local state for pagination and search
	const [currentPage, setCurrentPage] = useState(search.page);
	const [pageSize, setPageSize] = useState(search.limit);
	const [paginationMeta, setPaginationMeta] = useState({
		page: currentPage,
		limit: pageSize,
		total: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	});
	const [searchTerm, setSearchTerm] = useState(search.search);
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(search.search);

	// Debounce search term to avoid too many API calls
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
			setCurrentPage(1); // Reset to first page when search term changes
		}, 300); // 300ms delay

		return () => clearTimeout(timer);
	}, [searchTerm]);

	// Access control
	const can = useCan();
	const canManageCustomers = can(Action.MANAGE_CUSTOMERS);

	// Pagination handlers
	const handlePageChange = useCallback((page: number) => {
		setCurrentPage(page);
	}, []);

	const handlePageSizeChange = useCallback((limit: number) => {
		setPageSize(limit);
		setCurrentPage(1); // Reset to first page when changing page size
	}, []);

	const handleSearchChange = useCallback((newSearchTerm: string) => {
		setSearchTerm(newSearchTerm);
		// Page reset is handled by useEffect when debouncedSearchTerm changes
	}, []);

	const handlePaginationMetaChange = useCallback((meta: PaginationMeta) => {
		setPaginationMeta(meta);
	}, []);

	const displayedCount = useMemo(() => {
		if (!paginationMeta.total) {
			return 0;
		}

		const startIndex = (paginationMeta.page - 1) * paginationMeta.limit;
		const remaining = paginationMeta.total - startIndex;

		if (remaining <= 0) {
			return 0;
		}

		return Math.min(paginationMeta.limit, remaining);
	}, [paginationMeta.limit, paginationMeta.page, paginationMeta.total]);

	return (
		<div className="h-full flex flex-col space-y-2 sm:space-y-3">
			{/* Header */}
			<PageHeader
				title={t("customers.title")}
				description={t("customers.management.description")}
				showBackButton={false}
				actions={
					canManageCustomers ? (
						<Link
							to="/app/org/$orgId/customers/new"
							params={{ orgId }}
							className="btn btn-primary btn-sm"
							data-testid="customers-add-button"
							aria-label={t("customers.addCustomer")}
						>
							<Plus className="w-4 h-4" />
							<span className="hidden sm:inline">
								{t("customers.addCustomer")}
							</span>
						</Link>
					) : undefined
				}
			/>
			{/* Customers Card */}
			<ListPageCard>
				<ListPageCardHeader className="space-y-3">
					{/* Search */}
					<div className="relative flex-1 sm:max-w-md">
						<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-base-content/40" />
						<Input
							type="search"
							placeholder={t("customers.searchPlaceholder")}
							value={searchTerm}
							onChange={(e) => handleSearchChange(e.target.value)}
							className="pl-10 pr-10"
							data-testid="customers-search-input"
							autoComplete="off"
							data-1p-ignore="true"
							data-lpignore="true"
							data-form-type="other"
						/>
						<div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
							{searchTerm && (
								<button
									type="button"
									onClick={() => handleSearchChange("")}
									className="text-base-content/40 hover:text-base-content/80 transition-colors"
									aria-label={t("common.clear")}
								>
									<X className="h-3 w-3" />
								</button>
							)}
							{searchTerm !== debouncedSearchTerm && (
								<div className="h-4 w-4 animate-spin rounded-full border-b-2 border-primary" />
							)}
						</div>
					</div>
					{/* Results count */}
					<div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs text-base-content/60">
						<span className="whitespace-nowrap">
							{t("common.showingResults", {
								count: displayedCount,
								total: paginationMeta.total,
							})}
						</span>
						{debouncedSearchTerm && (
							<>
								<span className="text-base-content/30">·</span>
								<span
									className="badge badge-ghost badge-sm max-w-50 truncate"
									title={debouncedSearchTerm}
								>
									{t("common.search")}:
									<span className="ml-1 font-medium">
										{debouncedSearchTerm}
									</span>
								</span>
							</>
						)}
					</div>
				</ListPageCardHeader>
				<ListPageCardContent>
					{/* Table */}
					<div className="flex-1 overflow-hidden">
						<CustomerList
							organizationId={orgId}
							pagination={{
								page: currentPage,
								limit: pageSize,
							}}
							searchTerm={debouncedSearchTerm}
							userId={userId}
							token={token}
							canManageCustomers={canManageCustomers}
							onPaginationMetaChange={handlePaginationMetaChange}
						/>
					</div>
					{/* Pagination */}
					<div className="shrink-0">
						<Pagination
							pagination={paginationMeta}
							onPageChange={handlePageChange}
							onPageSizeChange={handlePageSizeChange}
						/>
					</div>
				</ListPageCardContent>
			</ListPageCard>
		</div>
	);
}
